import React, { FC, useMemo } from 'react'
import { FormattedDateTime } from 'transfix-ui/components/FormattedDateTime'
import { FormattedDateTimeRange } from 'transfix-ui/components/FormattedDateTimeRange'
import { Box, makeStyles, Typography } from 'transfix-ui/core'
import { brandColors } from 'transfix-ui/theme/colors'
import { BookableShipmentSavedSearch } from 'apps/fleet-planner/types'
import { fmtDaysStr, fmtEquipmentStr } from 'apps/fleet-planner/utils/bookableShipmentSavedSearch'
import { currentTimezone } from 'constants/AllowedTimezones'

interface BookableShipmentSavedSearchAlertRowSummaryProps {
  savedSearch: BookableShipmentSavedSearch
}

const useStyles = makeStyles(() => ({
  text: {
    color: brandColors.coolGray6,
  },
}))

export const BookableShipmentSavedSearchAlertRowSummary: FC<BookableShipmentSavedSearchAlertRowSummaryProps> = ({
  savedSearch,
}) => {
  const classes = useStyles()
  const Text = ({ children }: { children: React.ReactElement | string }) => (
    <Typography variant='body2' noWrap>
      {children}
    </Typography>
  )
  const content = useMemo(() => {
    const fragments = []
    const seperator = <>&nbsp;·&nbsp;</>
    // pickup
    const dayDescription = fmtDaysStr({
      days: savedSearch.pickupIsoDaysOfWeek,
    })
    if (dayDescription) {
      fragments.push(<Text>{`Pickup ${dayDescription || 'any day'}`}</Text>)
      fragments.push(seperator)
    } else if (savedSearch.pickupStartDate && savedSearch.pickupEndDate) {
      fragments.push(<Text>Pickup&nbsp;</Text>)
      fragments.push(
        <Text>
          <FormattedDateTimeRange
            startAt={savedSearch.pickupStartDate ?? ''}
            endAt={savedSearch.pickupEndDate ?? ''}
            startAtTimezone={currentTimezone}
            endAtTimezone={currentTimezone}
            format='dateAndYear'
            abbreviated
          />
        </Text>
      )
      fragments.push(seperator)
    } else if (savedSearch.pickupStartDate) {
      fragments.push(<Text>Pickup&nbsp;</Text>)
      fragments.push(
        <Text>
          <FormattedDateTime
            datetime={savedSearch.pickupStartDate}
            displayTimezone={currentTimezone}
            format='dateAndYear'
            abbreviated
          />
        </Text>
      )
      fragments.push(seperator)
    } else {
      fragments.push(<Text>Pickup any day</Text>)
      fragments.push(seperator)
    }
    // dropoff
    if (savedSearch.deliveryStartDate && savedSearch.deliveryEndDate) {
      fragments.push(<Text>Dropoff&nbsp;</Text>)
      fragments.push(
        <Text>
          <FormattedDateTimeRange
            startAt={savedSearch.deliveryStartDate ?? ''}
            endAt={savedSearch.deliveryEndDate ?? ''}
            startAtTimezone={currentTimezone}
            endAtTimezone={currentTimezone}
            format='dateAndYear'
          />
        </Text>
      )
      fragments.push(seperator)
    } else if (savedSearch.deliveryStartDate) {
      fragments.push(<Text>Dropoff&nbsp;</Text>)
      fragments.push(
        <Text>
          <FormattedDateTime
            datetime={savedSearch.deliveryStartDate}
            displayTimezone={currentTimezone}
            format='dateAndYear'
            abbreviated
          />
        </Text>
      )
      fragments.push(seperator)
    }
    // equipment
    const equipmentSummary = fmtEquipmentStr({ key: savedSearch.trailerType })
    if (equipmentSummary) {
      fragments.push(<Text>{equipmentSummary}</Text>)
      fragments.push(seperator)
    }
    // drop trailer
    if (savedSearch.dropTrailer) {
      fragments.push(<Text>Drop trailer only&nbsp;</Text>)
      fragments.push(seperator)
    }
    // clean up trailing seperator
    if (fragments.slice(-1)[0] === seperator) {
      fragments.pop()
    }
    return fragments
  }, [savedSearch])

  return (
    <Box className={classes.text} display='flex' flexWrap='wrap' alignItems='center'>
      {content}
    </Box>
  )
}
